import { Button, Checkbox, Divider, message, Popover, Space, Table, Typography } from "antd"
import React, { useEffect, useState } from "react"
import moment from "moment"
import { CheckSquareFilled, CloseOutlined, FileOutlined, FilterOutlined } from "@ant-design/icons"
import CreateInvoiceAsDrawer from "../../Invoices/Create/CreateInvoiceAsDrawer"
import "./WarehouseOrders.css"
import { OrderAndPrefix } from "../../../components/Orders/Helpers/OrderAndPrefix"
import { endDate, startDate } from "../../../components/Orders/Helpers/TableDetails"
import { doc, updateDoc } from "firebase/firestore"
import { db } from "../../../firebase/config"
import OrderCountDisplay from "../../../components/Table/orderCountDisplay"
import AddCustomInvoice from "../../../components/Orders/Helpers/AddCustomInvoice"
import AddCustomPackingList from "../../../components/Orders/Helpers/AddCustomPackingList"
import SplitOrderForManager from "../../../components/Manager/Order/Action/SplitOrderForManager"
import CombineOrdersDrawer from "../../../components/Manager/Order/Action/CombineOrdersDrawer"
import ComplaintDrawer from "../../../components/Orders/ComplaintDrawer"
import PackingListDrawer from "../../../components/Orders/PackingListDrawer"
import { MaterialVarianceValue } from "../../../utils/calcMaterialVariance"
import { filterOrdersByRole } from "../../../utils/filterOrdersByRole"
import { useAuthContext } from "../../../hooks/useAuthContext"

const { Text, Link } = Typography

export default function WarehouseAwaitingDocument({ initialData, loading }) {
  const [data, setData] = useState(initialData)
  const [selectedRowKeys, setSelectedRowKeys] = useState([])
  const [selectedRows, setSelectedRows] = useState([])

  const { admin, devSpecialist, semiAdmin } = useAuthContext()

  const [filterValues, setFilterValues] = useState({
    machine: true,
    dueDate: true,
    usedMetersCol: true,
  })

  const exactlyOneSelected = selectedRows.length === 1
  const selectedOrder = exactlyOneSelected ? selectedRows[0] : null

  const handleInvoiceDownload = async (recordId) => {
    try {
      const docRef = doc(db, "orders", recordId)
      await updateDoc(docRef, {
        invoiceDownloaded: true,
      })
    } catch (error) {
      console.error("Error updating Firestore document: ", error)
    }
  }
  const handleProformaDownload = async (recordId) => {
    try {
      const docRef = doc(db, "orders", recordId)
      await updateDoc(docRef, {
        proformaDownloaded: true,
      })
    } catch (error) {
      console.error("Error updating Firestore document: ", error)
    }
  }

  const handlePackingListDownload = async (recordId) => {
    try {
      const docRef = doc(db, "orders", recordId)
      await updateDoc(docRef, {
        packingListDownloaded: true,
      })
    } catch (error) {
      console.error("Error updating Firestore document: ", error)
    }
  }

  useEffect(() => {
    setTimeout(async () => {
      const filtered = filterOrdersByRole(initialData, { admin, semiAdmin, devSpecialist })

      setData(filtered)
    }, 200) //
  }, [initialData, admin, semiAdmin, devSpecialist])

  const [firstSelectedClient, setFirstSelectedClient] = useState(null)

  //selected rows
  const onSelectChange = (newSelectedRowKeys, selectedRows) => {
    setSelectedRowKeys(newSelectedRowKeys)
    setSelectedRows(selectedRows)

    //allows to select only one kind of client
    if (selectedRows.length === 1) {
      setFirstSelectedClient(selectedRows[0].client.client)
    } else if (selectedRows.length === 0) {
      setFirstSelectedClient(null)
    }
  }

  const rowSelection = {
    selectedRowKeys,
    onChange: onSelectChange,
    selections: [Table.SELECTION_ALL, Table.SELECTION_NONE],
    getCheckboxProps: (record) => ({
      disabled:
        (firstSelectedClient !== null && record.client.client !== firstSelectedClient) ||
        record.hasInvoice,
    }),
  }

  //client filter
  let clientFIlters =
    data &&
    data.reduce((accumulator, item) => {
      const clientName = item.client.client
      const existingItem = accumulator.find((filterItem) => filterItem.text === clientName)

      if (existingItem) {
        existingItem.value = clientName
      } else {
        accumulator.push({
          text: clientName,
          value: clientName,
        })
      }

      return accumulator
    }, [])

  let productFilters =
    data &&
    data.reduce((accumulator, item) => {
      const productName = item.selectedProduct.product.productName
      const existingItem = accumulator.find((filterItem) => filterItem.text === productName)

      if (existingItem) {
        existingItem.value = productName
      } else {
        accumulator.push({
          text: productName,
          value: productName,
        })
      }

      return accumulator
    }, [])

  // --- Handler for Custom Packing List Download ---
  const handleCustomPackingListDownload = async (recordId) => {
    try {
      const docRef = doc(db, "orders", recordId)
      // Use dot notation to update nested field
      await updateDoc(docRef, {
        "customPackingListData.downloaded": true,
      })
      // Update local state
      setData((prevData) =>
        prevData.map((item) =>
          item.id === recordId && item.customPackingListData
            ? {
                ...item,
                customPackingListData: { ...item.customPackingListData, downloaded: true },
              }
            : item,
        ),
      )
    } catch (error) {
      console.error("Error updating Firestore document (custom packing list): ", error)
      message.error("Failed to mark custom packing list as downloaded.")
    }
  }

  //filter content
  const returnFilterContent = () => {
    // Handler to update filter values
    const handleCheckboxChange = (column, checked) => {
      setFilterValues((prevValues) => ({
        ...prevValues,
        [column]: checked,
      }))
    }

    return (
      <div>
        <Space direction="vertical">
          <Checkbox
            onChange={(e) => handleCheckboxChange("orderNumber", e.target.checked)}
            checked={filterValues.orderNumber}>
            Order Number
          </Checkbox>
          <Checkbox
            onChange={(e) => handleCheckboxChange("machine", e.target.checked)}
            checked={filterValues.machine}>
            Machine
          </Checkbox>
          <Checkbox
            onChange={(e) => handleCheckboxChange("dueDate", e.target.checked)}
            checked={filterValues.dueDate}>
            Due Date
          </Checkbox>
          <Checkbox
            onChange={(e) => handleCheckboxChange("usedMetersCol", e.target.checked)}
            checked={filterValues.usedMetersCol}>
            Used
          </Checkbox>
        </Space>
      </div>
    )
  }

  // Helper Component for the Actions Popover
  const InvoiceActions = ({
    record,
    onInvoiceDownload,
    onProformaDownload,
    onPackingListDownload,
    onCustomPackingListDownload,
  }) => {
    // --- This is the same logic from your 'returnCorrectButton' function ---
    const getPopoverContent = () => {
      // Define flags for clarity, using nullish coalescing for safety
      const hasInvoice = record?.hasInvoice ?? false
      const hasStdPackingList = record?.hasPackingList ?? false
      const hasCustomPackingList = record?.customPackingList ?? false
      const hasProforma = record?.hasProforma ?? false

      // Check for data integrity for conditional rendering
      const invoiceUrl = record?.invoice?.invoiceUrl
      const stdPackingListUrl = record?.packingListUrl
      const proformaUrl = record?.proforma?.proformaUrl
      const customPLData = record?.customPackingListData
      const customPLUrl = customPLData?.documentDownloadUrl

      // We build an array of actions to check if we need to show the button at all
      const actions = []

      if (hasInvoice && invoiceUrl) actions.push("invoice")
      if (hasStdPackingList && stdPackingListUrl) actions.push("std_pl")
      if (!hasInvoice && !hasStdPackingList && hasProforma && proformaUrl) actions.push("proforma")
      actions.push("custom_pl_add") // Always show add custom PL
      if (!hasInvoice && !hasStdPackingList) actions.push("custom_invoice_add") // Always show add custom invoice
      if (hasCustomPackingList && customPLUrl) actions.push("custom_pl_view")

      // If there are no documents and no actions, return null
      if (actions.length === 0) {
        return null
      }

      return (
        <Space direction="vertical" align="start" size={4} style={{ width: "100%", padding: 5 }}>
          {/* --- Standard Invoice (Show if exists) --- */}
          {hasInvoice && invoiceUrl && (
            <a
              href={invoiceUrl}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => onInvoiceDownload(record.id)}
              style={{ display: "block" }}>
              <Button type="link" size="small" icon={<FileOutlined />}>
                {record.invoice?.name || "Invoice"}
                {record.invoiceDownloaded && <CheckSquareFilled style={{ marginLeft: 4 }} />}
              </Button>
            </a>
          )}

          {/* --- Standard Packing List (Show if exists) --- */}
          {hasStdPackingList && stdPackingListUrl && (
            <a
              href={stdPackingListUrl}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => onPackingListDownload(record.id)}
              style={{ display: "block" }}>
              <Button type="link" size="small" icon={<FileOutlined />}>
                Packing List
                {record.packingListDownloaded && <CheckSquareFilled style={{ marginLeft: 4 }} />}
              </Button>
            </a>
          )}

          {/* --- Proforma (Show ONLY if NO standard invoice/PL exist) --- */}
          {!hasInvoice && !hasStdPackingList && hasProforma && proformaUrl && (
            <a
              href={proformaUrl}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => onProformaDownload(record.id)}
              style={{ display: "block" }}>
              <Button type="link" size="small" icon={<FileOutlined />}>
                {record.proforma?.name || "Proforma"}
                {record.proformaDownloaded && <CheckSquareFilled style={{ marginLeft: 4 }} />}
              </Button>
            </a>
          )}

          {/* --- Custom Packing List Section (Link or Upload Button) --- */}
          {hasCustomPackingList && customPLUrl ? (
            <a
              href={customPLUrl}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => onCustomPackingListDownload(record.id)}
              style={{ display: "block" }}>
              <Button type="link" size="small" icon={<FileOutlined />}>
                {customPLData?.name || "Custom PL"}
                {customPLData?.downloaded && <CheckSquareFilled style={{ marginLeft: 4 }} />}
              </Button>
            </a>
          ) : (
            <AddCustomPackingList record={record} />
          )}

          {/* --- Add Custom Invoice Button (Show ONLY if NO standard invoice/PL exist) --- */}
          {!hasInvoice && !hasStdPackingList && <AddCustomInvoice record={record} />}

          {/* --- "No Document" Text (Show ONLY if NO documents/actions exist) --- */}
          {actions.length === 0 && (
            <Text type="secondary" style={{ paddingLeft: "4px" }}>
              No Document
            </Text>
          )}
        </Space>
      )
    }

    const content = getPopoverContent()

    // If there's no content to show, render a disabled or placeholder state
    if (!content) {
      return <Text type="secondary">No Actions</Text>
    }

    return (
      <Popover placement="left" color="#fff1b8" content={content} trigger="click">
        <Button type="primary" size="small">
          Actions
        </Button>
      </Popover>
    )
  }

  //default columns for table
  const defaultColumns = [
    {
      title: "Nr.",
      dataIndex: "orderNumber",
      align: "left",
      sorter: (a, b) => a.orderNumber - b.orderNumber,
      width: "10%",
      render: (value, record) => OrderAndPrefix(record),
    },
    {
      title: "Client",
      dataIndex: ["client", "client"],
      align: "left",
      width: "10%",
      filters: clientFIlters,
      onFilter: (value, record) => record.client.client.startsWith(value),
      filterSearch: true,
      ellipsis: true,
      render: (value) => {
        return (
          <>
            <Text copyable={{ text: value }}>{value}</Text>
          </>
        )
      },
    },
    {
      title: "Product",
      dataIndex: ["selectedProduct", "product", "productName"],
      align: "left",
      filters: productFilters,
      onFilter: (value, record) => record.selectedProduct.product.productName === value,
      filterSearch: true,
      width: "20%",
      render: (value) => {
        return (
          <>
            <Text copyable={{ text: value }}>{value}</Text>
          </>
        )
      },
    },
    {
      title: "Manufacturer",
      dataIndex: ["manufacturer", "company"],
      align: "left",
      ellipsis: true,
      // render: (val, rec) => <button onClick={() => console.log(rec)}></button>,
    },
    {
      title: "Box count",
      dataIndex: "boxesCount",
      width: "7%",
      align: "center",
      render: (value, record) => {
        if (record.selectedProduct.machine === "Tape") {
          return (
            <>
              <Text>{record.boxCount}</Text>
            </>
          )
        } else {
          return (
            <>
              <Text>{value}</Text>
            </>
          )
        }
      },
    },
    {
      title: "Count",
      dataIndex: "amountCreated",
      align: "center",
      // render: (value, record) => <button onClick={() => console.log(record)}>log</button>,
      render: (value, record) => <OrderCountDisplay value={value} record={record} />,
    },
    {
      title: "Variance",
      key: "variance",
      align: "center",
      width: "8%",
      render: (_, record) => <MaterialVarianceValue order={record} />,
    },
    {
      title: "Invoice",
      align: "center",
      width: "5%",
      render: (text, record) => (
        <InvoiceActions
          record={record}
          onInvoiceDownload={handleInvoiceDownload}
          onProformaDownload={handleProformaDownload}
          onPackingListDownload={handlePackingListDownload}
          onCustomPackingListDownload={handleCustomPackingListDownload}
        />
      ),
    },
    {
      title: "Technical",
      dataIndex: "technicalPage",
      align: "center",
      render: (value) => {
        if (value) {
          return (
            <Link target="_blank" href={value}>
              Link
            </Link>
          )
        } else {
          return <CloseOutlined />
        }
      },
    },
    {
      title: "Machine",
      dataIndex: ["selectedProduct", "machine"],
      align: "center",
      hidden: filterValues.machine,
    },
    {
      title: "Started",
      dataIndex: "created",
      align: "center",
      width: "8%",
      sorter: (a, b) => a.created.seconds - b.created.seconds,
      render: (value) => startDate(value),
    },
    {
      title: "Ended",
      align: "center",
      width: "8%",
      dataIndex: "doneByRewinder",
      sorter: (a, b) => a.doneByRewinder.seconds - b.doneByRewinder.seconds,
      render: (value, record) => endDate(value, record),
    },
    {
      title: "Due date",
      dataIndex: "orderDueDate",
      align: "center",
      width: "10%",

      hidden: filterValues.dueDate,
      sorter: (a, b) => a.orderDueDate.seconds - b.orderDueDate.seconds,
      render: (value) => <Text>{moment.unix(value.seconds).format("DD.MM.YYYY")}</Text>,
    },
  ]

  // Filter out columns based on filterValues before passing them to the Table
  const visibleColumns = defaultColumns.filter((column) => !filterValues[column.dataIndex])

  //handles the reset of the rowkeys
  const handleResetRows = () => {
    setSelectedRowKeys([])
    setSelectedRows([])
    setFirstSelectedClient(null)
  }

  return (
    <>
      <Space split={<Divider type="vertical" />}>
        <CreateInvoiceAsDrawer
          selectedRows={selectedRows}
          selectedRowKeys={selectedRowKeys}
          resetRows={handleResetRows}
        />

        <Space>
          <SplitOrderForManager selectedRows={selectedRows} onDone={handleResetRows} />

          <CombineOrdersDrawer selectedRows={selectedRows} onDone={handleResetRows} />
        </Space>

        <ComplaintDrawer
          orderData={selectedOrder}
          disabled={!exactlyOneSelected}
          buttonText="Create Complaint"
        />

        <PackingListDrawer
          orderData={selectedOrder}
          disabled={!exactlyOneSelected}
          buttonText="Packing List"
        />

        <Popover placement="bottom" trigger="click" content={returnFilterContent}>
          <Button icon={<FilterOutlined />}>Show/Hide Columns</Button>
        </Popover>
      </Space>

      <Table
        style={{ marginTop: "15px" }}
        size="small"
        loading={loading}
        columns={visibleColumns}
        bordered
        dataSource={data}
        // rowClassName={(record) => (removingOrderIds.includes(record.id) ? "fade-out" : "")}
        rowSelection={rowSelection}
        rowKey={(record) => record.id}
        pagination={{ defaultPageSize: "12", showSizeChanger: true }}
      />
    </>
  )
}
